import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Toolbar, useMediaQuery, Button, Grid, Paper, List, ListItem, ListItemIcon, Stack, Container, Card, CardContent, Divider, LinearProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import blitzLogo from '../../../Components/globalAssets/logo_placeholder.png';
import { motion } from "framer-motion";
import {
  Bolt,
  Paid,
  Receipt,
  TrendingUp,
  Sync,
  AccountBalance,
  CheckCircle,
  CurrencyExchange,
  Schedule,
  Security,
  Speed,
  Analytics,
  Verified
} from "@mui/icons-material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import paymentImage from "../../../Components/globalAssets/6455048.gif";
import MagicCard from "../../../Components/MagicCard";
import { BentoGrid, BentoGridItem } from "../../../Components/BentoGrid";

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } };
const fadeInLeft = { hidden: { opacity: 0, x: -40 }, visible: { opacity: 1, x: 0 } };
const fadeInRight = { hidden: { opacity: 0, x: 40 }, visible: { opacity: 1, x: 0 } };

const BlitzPayFeatures = [
  {
    icon: <Bolt fontSize="large" />,
    title: "Instant Payments",
    description: "Convert Net 30/60 terms to immediate cash with our instant payment options",
    metric: "< 1 hour",
    color: "#2196F3",
    gradient: "linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)",
    colSpan: 2
  },
  {
    icon: <Receipt fontSize="large" />,
    title: "Automated Invoicing",
    description: "Generate professional invoices with automated reminders and tracking",
    metric: "99.9% accuracy",
    color: "#00BCD4",
    gradient: "linear-gradient(135deg, #00BCD4 0%, #4DD0E1 100%)",
    colSpan: 1
  },
  {
    icon: <TrendingUp fontSize="large" />,
    title: "Cash Flow Optimization",
    description: "Manage your cash with our built in reporting technology",
    metric: "30% improvement",
    color: "#4CAF50",
    gradient: "linear-gradient(135deg, #4CAF50 0%, #81C784 100%)",
    colSpan: 1
  },
  {
    icon: <Sync fontSize="large" />,
    title: "Payment Automation",
    description: "Schedule recurring payments and automate approvals workflows",
    metric: "95% automation",
    color: "#FF9800",
    gradient: "linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)",
    colSpan: 2
  }
];

const trustIndicators = [
  { icon: <Security />, label: "Bank-level Security" },
  { icon: <CheckCircle />, label: "99.99% Uptime" }
];

export default function BlitzPay() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const refs = useRef({});
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  const handleNavigate = () => navigate(routes.onboard);
  const handleNavigate1 = () => navigate(routes.help);

  const ProfessionalFeatureCard = ({ icon, title, description, metric, color }) => (
    <Card
      component={motion.div}
      variants={fadeInUp}
      whileHover={{ y: -4 }}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
        "&:hover": {
          boxShadow: `0 12px 40px ${color}20`,
          borderColor: color,
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: 4,
          background: `linear-gradient(90deg, ${color} 0%, ${color}80 100%)`,
        }
      }}
    >
      <CardContent sx={{ p: 4, height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}15`,
              color: color,
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: color, fontWeight: 600 }}>
              {metric}
            </Typography>
          </Box>
        </Box>
        <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  const StatCard = ({ value, label, color = "#667eea" }) => (
    <motion.div variants={fadeInUp}>
      <MagicCard
        gradientFrom={color}
        gradientTo={color}
        gradientColor={color}
        gradientOpacity={0.06}
        gradientSize={200}
        sx={{
          p: { xs: 3, md: 4 },
          textAlign: "center",
          border: "1px solid rgba(0,0,0,0.04)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            color: "#0f172a",
            mb: 0.5,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          {value}
        </Typography>
        <Typography
          sx={{
            color: "#64748b",
            fontWeight: 400,
            fontSize: "0.85rem",
          }}
        >
          {label}
        </Typography>
      </MagicCard>
    </motion.div>
  );

  return (
    <Box sx={{ bgcolor: theme.palette.background.default }}>
      <HomeHeader />
      <Toolbar />

      {/* Professional Hero Section */}
      <Box
        component="section"
        ref={el => (refs.current['hero'] = el)}
        sx={{
          py: { md: 8 },
          
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1,px: {  md:8 }, }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              alignItems: "center",
              gap: { xs: 4, lg: 0 },
            }}
          >
            {/* Left Side - Hero Text */}
            <Box sx={{ flex: 1, maxWidth: { lg: "50%" } }}>
              <motion.div variants={fadeInLeft} initial="hidden" animate="visible">
                <Box sx={{ mb: 3 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: { xs: "0.60rem", md: "0.65rem" },
                        fontWeight: 600,
                        letterSpacing: "0.15em",
                        mb: 2,
                        display: "block",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 0 10px 0 rgba(0,0,0,0.05)",
                        borderRadius: "50px",
                        padding: "2px 10px",
                        width: "fit-content",
                        background: "white"
                      }}
                    >
                      BLITZPAY FINANCIAL SOLUTIONS
                    </Typography>
                  </motion.div>
                  <Typography
                    variant={isMobile ? "h3" : "h2"}
                    sx={{
                      fontWeight: 500,
                      mb: 3,
                      fontFamily: "Moret Variable",
                      color: theme.palette.text.primary,
                      lineHeight: 1.2,
                      fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                    }}
                  >
                    Enterprise-Grade <span style={{ color: theme.palette.primary.main }}>Payment Solutions</span>
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                      lineHeight: 1.6,
                      maxWidth: 550,
                    }}
                  >
                    Streamline creator payments with instant settlements, automated invoicing, and intelligent cash flow management
                  </Typography>
                </Box>

                {/* Trust Indicators */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {trustIndicators.map((indicator, index) => (
                      <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                        <Box sx={{ color: theme.palette.primary.main, mr: 1 }}>
                          {indicator.icon}
                        </Box>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {indicator.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleNavigate}
                    sx={{
                      fontSize: { xs: "1rem", md: "1.125rem" },
                      px: { xs: 5, md: 8 },
                      py: { xs: 2, md: 2.5 },
                      fontWeight: 700,
                      borderRadius: "50px",
                      textTransform: "none",
                      boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                      position: "relative",
                      overflow: "hidden",
                      '&::before': {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "-100%",
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                        transition: "left 0.5s",
                      },
                      '&:hover': {
                        bgcolor: `${theme.palette.primary.light}`,
                        transform: 'translateY(-4px) scale(1.02)',
                        boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                        '&::before': {
                          left: '100%'
                        }
                      },
                      transition: "all 0.3s ease"
                    }}
                  >
                    <span style={{ position: "relative", zIndex: 1 }}>
                      Onboard Today
                    </span>
                  </Button>
                </Box>
              </motion.div>
            </Box>

            {/* Right Side - Blitz Logo with Floating Cards */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { lg: "45%" },
                display: { xs: 'none', lg: 'flex' },
                justifyContent: "flex-end",
              }}
            >
              <motion.div
                variants={fadeInRight}
                initial="hidden"
                animate="visible"
                transition={{ delay: 0.3 }}
                style={{ width: "100%" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 420,
                    height: 360,
                    ml: "auto",
                  }}
                >
                  {/* Floating Card - Top Left */}
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    style={{ position: "absolute", top: 0, left: 0, zIndex: 2 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                        boxShadow: "0 8px 32px rgba(15,23,42,0.08)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "#4CAF5015",
                          color: "#4CAF50",
                        }}>
                          <TrendingUp />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                            Revenue Growth
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: "#4CAF50", fontSize: "1.1rem", lineHeight: 1 }}>
                            +127%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>

                  {/* Floating Card - Top Right */}
                  <motion.div
                    animate={{ y: [0, -12, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                    style={{ position: "absolute", top: 20, right: 0, zIndex: 2 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                        boxShadow: "0 8px 32px rgba(15,23,42,0.08)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "#2196F315",
                          color: "#2196F3",
                        }}>
                          <Speed />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                            Payout Speed
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: "#2196F3", fontSize: "1.1rem", lineHeight: 1 }}>
                            {"<1hr"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>

                  {/* Center Blitz Logo */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    
                    <Typography
                      sx={{
                        fontSize: { lg: "3rem", xl: "4rem" },
                        fontWeight: 800,
                        color: "#0f172a",
                        letterSpacing: "-0.02em",
                      }}
                    >
                     <img src={blitzLogo} alt="Blitz" style={{ width: "100%", height: "100%" }} />
                    </Typography>
                    </Box>

                  {/* Floating Card - Bottom Left */}
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                    style={{ position: "absolute", bottom: 0, left: 20, zIndex: 2 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                        boxShadow: "0 8px 32px rgba(15,23,42,0.08)",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: "#FF980015",
                          color: "#FF9800",
                        }}>
                          <Analytics />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: "0.7rem" }}>
                            Success Rate
                          </Typography>
                          <Typography sx={{ fontWeight: 700, color: "#FF9800", fontSize: "1.1rem", lineHeight: 1 }}>
                            97%
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </motion.div>
                </Box>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Performance Metrics */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: theme.palette.background.default }}>
        <Container maxWidth="lg">
          <motion.div variants={fadeInUp} initial="hidden" animate="visible">
            <Typography
              variant="h4"
              align="center"
              sx={{ fontWeight: 600, mb: 6, color: theme.palette.text.primary, fontFamily:"Moret Variable" ,fontWeight:'500',             fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" }, }}
            >
              Proven Performance Metrics
            </Typography>
          </motion.div>
          <Grid container spacing={3}>
            <Grid item xs={6} md={3}>
              <StatCard value="90%" label="Faster Payments" color="#667eea" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard value="12h" label="Average Payout Time" color="#f5576c" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard value="3.8x" label="Faster Invoice Clearance" color="#00f2fe" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard value="97%" label="Payment Success Rate" color="#feca57" />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Feature Grid */}
      <Box
        id="features"
        ref={el => (refs.current['features'] = el)}
        sx={{ py: { xs: 8, md: 10 },  }}
      >
        <Container maxWidth="lg">
          <motion.div variants={fadeInUp} initial="hidden" animate="visible">
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: theme.palette.text.primary,
                  fontFamily:"Moret Variable" ,fontWeight:'500',             fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" },
                }}
              >
                Comprehensive Payment Infrastructure
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: "auto",
                }}
              >
                Built for scale, designed for simplicity
              </Typography>
            </Box>
          </motion.div>

          <BentoGrid>
            {BlitzPayFeatures.map((feature, index) => (
              <BentoGridItem
                key={index}
                title={feature.title}
                description={feature.description}
                colSpan={feature.colSpan}
                gradient={feature.gradient}
                icon={
                  <Box sx={{ color: "white", display: "flex" }}>
                    {feature.icon}
                  </Box>
                }
                header={
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: `${feature.color}15`,
                        color: feature.color,
                        display: "flex",
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography
                      sx={{
                        px: 2,
                        py: 0.5,
                        borderRadius: "20px",
                        background: feature.gradient,
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {feature.metric}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </BentoGrid>
        </Container>
      </Box>

      {/* Process Workflow */}
      <Box
        id="how-it-works"
        ref={el => (refs.current['how-it-works'] = el)}
        sx={{ py: { xs: 8, md: 10 }, bgcolor: theme.palette.background.default }}
      >
        <Container maxWidth="lg">
          <motion.div variants={fadeInUp} initial="hidden" animate="visible">
            <Typography
              variant="h3"
              align="center"
              sx={{ fontWeight: 700, mb: 8, color: theme.palette.text.primary, fontFamily:"Moret Variable" ,fontWeight:'500',             fontSize: { xs: "2.5rem", md: "3.5rem", lg: "4rem" }, }}
            >
              Streamlined Payment Workflow
            </Typography>
          </motion.div>

          {/* Modern Step Cards */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {[
              {
                icon: <Receipt sx={{ fontSize: 28 }} />,
                title: "Invoice Submission",
                description: "Automated invoice processing with OCR technology and validation",
                color: "#2196F3",
                step: "01"
              },
              {
                icon: <Schedule sx={{ fontSize: 28 }} />,
                title: "Terms Configuration",
                description: "Flexible payment terms with instant liquidity options",
                color: "#00BCD4",
                step: "02"
              },
              {
                icon: <CurrencyExchange sx={{ fontSize: 28 }} />,
                title: "Smart Processing",
                description: "Real-time currency conversion at institutional rates",
                color: "#4CAF50",
                step: "03"
              },
              {
                icon: <Paid sx={{ fontSize: 28 }} />,
                title: "Instant Settlement",
                description: "Direct bank deposits with real-time confirmation",
                color: "#FF9800",
                step: "04"
              }
            ].map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  variants={fadeInUp}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.1 }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      p: 3,
                      pt: 4,
                      height: "100%",
                      background: "white",
                      borderRadius: "20px",
                      border: "1px solid rgba(0,0,0,0.04)",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      overflow: "hidden",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
                        "& .step-number": {
                          transform: "scale(1.1)",
                          color: step.color,
                        },
                        "& .step-icon": {
                          background: step.color,
                          color: "white",
                        },
                      },
                    }}
                  >
                    {/* Step Number - Large watermark style */}
                    <Typography
                      className="step-number"
                      sx={{
                        position: "absolute",
                        top: 12,
                        right: 16,
                        fontSize: "3.5rem",
                        fontWeight: 800,
                        color: "rgba(0,0,0,0.04)",
                        lineHeight: 1,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {step.step}
                    </Typography>

                    {/* Icon */}
                    <Box
                      className="step-icon"
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "14px",
                        background: `${step.color}12`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: step.color,
                        mb: 2.5,
                        transition: "all 0.3s ease",
                      }}
                    >
                      {step.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#0f172a",
                        fontSize: "1.05rem",
                        mb: 1,
                      }}
                    >
                      {step.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontSize: "0.85rem",
                        lineHeight: 1.6,
                      }}
                    >
                      {step.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Comparison Cards - Vertical Layout */}
          <Grid container spacing={3}>
            {[
              { 
                title: "Net 30 Terms", 
                time: "30+ days", 
                fee: "0%", 
                impact: "Negative",
                isHighlighted: false 
              },
              { 
                title: "BlitzPay", 
                time: "Instant", 
                fee: "4-10%", 
                impact: "Optimized",
                isHighlighted: true 
              },
              { 
                title: "Standard Wire", 
                time: "3-5 days", 
                fee: "2-3%", 
                impact: "Moderate",
                isHighlighted: false 
              },
            ].map((plan, index) => (
              <Grid item xs={12} md={4} key={plan.title}>
                <motion.div
                  variants={fadeInUp}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <Box
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderRadius: "20px",
                      background: plan.isHighlighted 
                        ? "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)" 
                        : "white",
                      border: plan.isHighlighted 
                        ? "1px solid rgba(15,23,42,0.12)" 
                        : "1px solid rgba(0,0,0,0.05)",
                      boxShadow: plan.isHighlighted 
                        ? "0 8px 32px rgba(15,23,42,0.1)" 
                        : "0 2px 12px rgba(0,0,0,0.04)",
                      textAlign: "center",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: plan.isHighlighted 
                          ? "0 20px 50px rgba(15,23,42,0.15)" 
                          : "0 12px 32px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    {plan.isHighlighted && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "20px",
                          background: "transparent",
                          color: "#667eea",
                          fontWeight: 700,
                          fontSize: "0.6rem",
                          border: "1px solid #667eea",
                        }}
                      >
                        RECOMMENDED
                      </Box>
                    )}
                    
                    {/* Title */}
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        color: "#0f172a",
                        mb: 3,
                      }}
                    >
                      {plan.title}
                    </Typography>

                    {/* Payment Time - Hero stat */}
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "2.5rem",
                          color: "#0f172a",
                          lineHeight: 1,
                        }}
                      >
                        {plan.time}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: "#94a3b8",
                          mt: 1,
                        }}
                      >
                        Payment Time
                      </Typography>
                    </Box>

                    {/* Fee */}
                    <Box sx={{ 
                      py: 2, 
                      px: 2, 
                      borderRadius: "12px", 
                      background: "rgba(0,0,0,0.025)",
                      mb: 2,
                    }}>
                      <Typography sx={{ 
                        fontSize: "0.7rem", 
                        color: "#94a3b8",
                        mb: 0.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}>
                        Fee
                      </Typography>
                      <Typography sx={{ 
                        fontWeight: 600, 
                        fontSize: "1.1rem",
                        color: "#0f172a" 
                      }}>
                        {plan.fee}
                      </Typography>
                    </Box>

                    {/* Cash Flow */}
                    <Box sx={{ 
                      py: 2, 
                      px: 2, 
                      borderRadius: "12px", 
                      background: "rgba(0,0,0,0.025)",
                    }}>
                      <Typography sx={{ 
                        fontSize: "0.7rem", 
                        color: "#94a3b8",
                        mb: 0.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                      }}>
                        Cash Flow Impact
                      </Typography>
                      <Typography sx={{ 
                        fontWeight: 600, 
                        fontSize: "1.1rem",
                        color: plan.impact === "Optimized" ? "#22c55e" : plan.impact === "Negative" ? "#ef4444" : "#0f172a"
                      }}>
                        {plan.impact}
                      </Typography>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Enterprise CTA */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Navy blue radial gradient backgrounds */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "400px", md: "800px" },
            height: { xs: "400px", md: "800px" },
            background: "radial-gradient(circle, rgba(15,23,42,0.08) 0%, rgba(99,102,241,0.04) 40%, transparent 70%)",
            borderRadius: "50%",
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "30%",
            left: "20%",
            width: { xs: "200px", md: "400px" },
            height: { xs: "200px", md: "400px" },
            background: "radial-gradient(circle, rgba(110, 132, 231, 0.39) 0%, transparent 60%)",
            borderRadius: "50%",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "20%",
            right: "15%",
            width: { xs: "250px", md: "500px" },
            height: { xs: "250px", md: "500px" },
            background: "radial-gradient(circle, rgba(79, 172, 254, 0.17) 0%, transparent 60%)",
            borderRadius: "50%",
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div variants={fadeInUp} initial="hidden" animate="visible">
            <Box sx={{ textAlign: "center" }}>
              
                <Analytics sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              
              <Typography
                variant={isMobile ? "h3" : "h2"}
                sx={{ 
                  fontWeight: 500, 
                  mb: 3, 
                  color: "#0f172a", 
                  fontFamily: "Moret Variable", 
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                Ready to Optimize Your Payments?
              </Typography>
              <Typography
                variant="h6"
                sx={{ 
                  mb: 5, 
                  fontWeight: 400, 
                  maxWidth: 550, 
                  mx: "auto", 
                  color: "#64748b",
                  lineHeight: 1.6,
                }}
              >
                Join enterprise clients processing millions annually with BlitzPay's proven infrastructure
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNavigate}
                  sx={{
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    px: { xs: 5, md: 8 },
                    py: { xs: 2, md: 2.5 },
                    fontWeight: 700,
                    borderRadius: "50px",
                    textTransform: "none",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                    position: "relative",
                    overflow: "hidden",
                    '&::before': {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                      transition: "left 0.5s",
                    },
                    '&:hover': {
                      bgcolor: `${theme.palette.primary.light}`,
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 16px 56px rgba(0,0,0,0.2)',
                      '&::before': {
                        left: '100%'
                      }
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  <span style={{ position: "relative", zIndex: 1 }}>
                    Onboard Today
                  </span>
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleNavigate1}
                  sx={{
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    px: { xs: 5, md: 8 },
                    py: { xs: 2, md: 2.5 },
                    fontWeight: 700,
                    borderRadius: "50px",
                    textTransform: "none",
                    borderColor: "#cbd5e1",
                    borderWidth: 1,
                    color: "#334155",
                    background: "white",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      background: "#f1f5f9",
                      color: "#0f172a",
                      transform: 'translateY(-4px)',
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  Contact Us
                </Button>
              </Stack>
            </Box>
          </motion.div>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}